import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, retry } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  PresalesClient,
  CreatePresalesClient,
  PresalesCostCenter,
  CreatePresalesCostCenter,
  PresalesProject,
  CreatePresalesProject,
  SalesStage,
  PromoteToDeliveryRequest,
  PromoteResult,
  CurrencyRate
} from '../models/presales.model';

@Injectable({ providedIn: 'root' })
export class PresalesService {
  private http = inject(HttpClient);
  private clientUrl  = `${environment.apiUrl}/PresalesClient`;
  private projectUrl = `${environment.apiUrl}/PresalesProject`;
  private costCenterUrl = `${environment.apiUrl}/PresalesCostCenter`;
  private currencyRateUrl = `${environment.apiUrl}/CurrencyRate`;

  // ================= Currency rates (USD KPI cards) =================

  /** Cached USD conversion rates. Backend auto-refreshes from a free FX provider. */
  getCurrencyRates(): Observable<CurrencyRate[]> {
    return this.http.get<CurrencyRate[]>(this.currencyRateUrl).pipe(retry(2));
  }

  // ================= Presales Clients =================

  getAllClients(): Observable<PresalesClient[]> {
    return this.http.get<PresalesClient[]>(this.clientUrl).pipe(retry(2));
  }

  getClient(id: string): Observable<PresalesClient> {
    return this.http.get<PresalesClient>(`${this.clientUrl}/${id}`);
  }

  addClient(dto: CreatePresalesClient): Observable<PresalesClient> {
    return this.http.post<PresalesClient>(this.clientUrl, dto);
  }

  updateClient(id: string, dto: CreatePresalesClient): Observable<PresalesClient> {
    return this.http.put<PresalesClient>(`${this.clientUrl}/${id}`, dto);
  }

  /** Soft-delete: backend sets IsActive = false. Row remains for reporting. */
  deleteClient(id: string): Observable<void> {
    return this.http.delete<void>(`${this.clientUrl}/${id}`);
  }

  // ================= Presales Cost Centers =================

  /**
   * All presales cost centers. Pass a client id to scope the list to that client's
   * cost centers PLUS the shared (no-client) ones — what the project form dropdown needs.
   */
  getAllCostCenters(presalesClientId?: string | null): Observable<PresalesCostCenter[]> {
    const url = presalesClientId
      ? `${this.costCenterUrl}?presalesClientId=${presalesClientId}`
      : this.costCenterUrl;
    return this.http.get<PresalesCostCenter[]>(url).pipe(retry(2));
  }

  getCostCenter(id: string): Observable<PresalesCostCenter> {
    return this.http.get<PresalesCostCenter>(`${this.costCenterUrl}/${id}`);
  }

  addCostCenter(dto: CreatePresalesCostCenter): Observable<PresalesCostCenter> {
    return this.http.post<PresalesCostCenter>(this.costCenterUrl, dto);
  }

  updateCostCenter(id: string, dto: CreatePresalesCostCenter): Observable<PresalesCostCenter> {
    return this.http.put<PresalesCostCenter>(`${this.costCenterUrl}/${id}`, dto);
  }

  /** Soft-delete: backend sets IsActive = false. Row remains for reporting. */
  deleteCostCenter(id: string): Observable<void> {
    return this.http.delete<void>(`${this.costCenterUrl}/${id}`);
  }

  // ================= Presales Projects =================

  getAllProjects(salesStage?: SalesStage): Observable<PresalesProject[]> {
    const url = salesStage ? `${this.projectUrl}?salesStage=${salesStage}` : this.projectUrl;
    return this.http.get<PresalesProject[]>(url).pipe(retry(2));
  }

  getProject(id: string): Observable<PresalesProject> {
    return this.http.get<PresalesProject>(`${this.projectUrl}/${id}`);
  }

  addProject(dto: CreatePresalesProject): Observable<PresalesProject> {
    return this.http.post<PresalesProject>(this.projectUrl, dto);
  }

  updateProject(id: string, dto: CreatePresalesProject): Observable<PresalesProject> {
    return this.http.put<PresalesProject>(`${this.projectUrl}/${id}`, dto);
  }

  /** Soft-delete: backend sets IsActive = false. */
  deleteProject(id: string): Observable<void> {
    return this.http.delete<void>(`${this.projectUrl}/${id}`);
  }

  /**
   * Promote a Won presales project into the main delivery system. Atomically creates a
   * Client (or links to an existing main-Clients row) + creates a Project + marks the
   * presales rows as promoted.
   */
  promoteProject(presalesProjectId: string, req: PromoteToDeliveryRequest): Observable<PromoteResult> {
    return this.http.post<PromoteResult>(`${this.projectUrl}/${presalesProjectId}/promote`, req);
  }
}
